import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  I2gNgTrackingActionBuilder,
  I2gNgTrackingService,
  ITrackingAction,
  ITrackingObject,
} from 'i2g-ng-tracking';
import { Observable, Subject } from 'rxjs';
import {
  filter,
  flatMap,
  map,
  shareReplay,
  take,
  takeUntil,
} from 'rxjs/operators';
import { LocalizationStore } from '../../../localization/store/stores';
import { TrackingScreenNamesEnum } from '../../../tracking/tracking.data';
import { IAccountResponseError } from '../../account.data';
import { AccountDispatcher } from '../../store/dispatchers';
import { AccountStore } from '../../store/stores';
import { LoginBuilder } from './login.builder';
import { IRouter } from 'i2g-ng-ui';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [LoginBuilder],
})
export class LoginComponent implements OnInit, OnDestroy, AfterViewInit {
  private _destroyed: Subject<boolean> = new Subject<boolean>();

  registerRoute: IRouter;
  trackingScreenName: TrackingScreenNamesEnum;
  trackingObject$: Observable<ITrackingObject>;
  emailLoginTrackingAction: ITrackingAction;

  error$: Observable<string | Error | IAccountResponseError>;
  processing$: Observable<boolean>;

  constructor(
    private _loginBuilder: LoginBuilder,
    private _localizationStore: LocalizationStore,
    private _accountStore: AccountStore,
    private _accountDispatchers: AccountDispatcher,
    private _trackingService: I2gNgTrackingService,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder
  ) {}

  ngOnInit() {
    this.trackingScreenName = TrackingScreenNamesEnum.account_sign_in;
    this.trackingObject$ = this._accountStore
      .getTrackingObject()
      .pipe(shareReplay(1));
    this.emailLoginTrackingAction = this._trackingActionBuilder.buildButtonTapped(
      'sign_in',
      this.trackingScreenName
    );

    this.registerRoute = this._loginBuilder.buildRegisterRoute();

    this.processing$ = this._accountStore
      .isAccountLoggingIn()
      .pipe(takeUntil(this._destroyed), shareReplay(1));

    this.error$ = this._accountStore.getAccountError().pipe(
      takeUntil(this._destroyed),
      map((error: Error | IAccountResponseError) => {
        if (error) {
          return error instanceof Error ? error.message : error.description;
        }
        return error;
      }),
      shareReplay(1)
    );
  }

  ngAfterViewInit() {
    this._trackingService.trackPage(this.trackingScreenName);
  }

  loginByEmailAndPassword(email: string, password: string) {
    this.processing$
      .pipe(
        filter((processing) => !processing),
        flatMap(() => this._localizationStore.getCurrentLocale()),
        take(1)
      )
      .subscribe((locale: string) =>
        this._accountDispatchers.loginAccountByEmail(
          email,
          password,
          locale,
          this.trackingScreenName
        )
      );
  }

  loginAccountByFacebook(email: string, idToken: string) {
    this.processing$
      .pipe(
        filter((processing) => !processing),
        flatMap(() => this._localizationStore.getCurrentLocale()),
        take(1)
      )
      .subscribe((locale) =>
        this._accountDispatchers.loginAccountByFacebook(
          email,
          idToken,
          locale,
          this.trackingScreenName
        )
      );
  }

  loginAccountByGoogle(email: string, idToken: string) {
    this.processing$
      .pipe(
        filter((processing) => !processing),
        flatMap(() => this._localizationStore.getCurrentLocale()),
        take(1)
      )
      .subscribe((locale) =>
        this._accountDispatchers.loginAccountByGoogle(
          email,
          idToken,
          locale,
          this.trackingScreenName
        )
      );
  }

  loginAccountByApple(email: string, idToken: string) {
    this.processing$
      .pipe(
        filter((processing) => !processing),
        flatMap(() => this._localizationStore.getCurrentLocale()),
        take(1)
      )
      .subscribe((locale) =>
        this._accountDispatchers.loginAccountByApple(
          email,
          idToken,
          locale,
          this.trackingScreenName
        )
      );
  }

  setErrorMessage(error: string) {
    this._accountDispatchers.loginAccountError(
      new Error(error),
      this.trackingScreenName
    );
  }

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
